/**
 * Public-browse marketplace grid. Adapted from
 * packages/dashboard/src/components/MarketplacePane.tsx with the
 * daemon-bound props (installedIds, onUpload) removed — the public
 * site has no concept of "installed" because there's no daemon
 * connection on this origin.
 *
 * Trust-badge logic mirrors PR #0: the green check next to a name
 * renders ONLY when `kind === "strykr" && signerVerified === true`
 * (build-time cryptographic check), and a low-trust dashed
 * "self-claimed" pill renders when `verified` is set without the
 * cryptographic backing.
 */
import { useMemo, useState } from "react";
import { Icon } from "./Icon.js";
import { StarRating } from "./StarRating.js";
import { VoteWidget } from "./VoteWidget.js";
import type { Strategy, StrategyCategory } from "../lib/types.js";
import { VENUES, VENUE_LABELS, type VenueId } from "../lib/taxonomy.js";

export interface MarketplaceGridProps {
  catalog: Strategy[];
  /** Open the strategy detail dialog. */
  onOpen: (strategy: Strategy) => void;
}

type SortKey = "popular" | "rating" | "newest" | "price-low" | "price-high";

const SORT_LABELS: Record<SortKey, string> = {
  popular: "Most installed",
  rating: "Top rated",
  newest: "Newest",
  "price-low": "Price: low to high",
  "price-high": "Price: high to low",
};

const ALL_CATEGORIES: Array<"All" | StrategyCategory> = [
  "All",
  "momentum",
  "mean-reversion",
  "funding-arb",
  "basis",
  "volatility",
  "macro",
  "ml-signal",
];

const CATEGORY_LABELS: Record<StrategyCategory, string> = {
  momentum: "Momentum",
  "mean-reversion": "Mean reversion",
  "funding-arb": "Funding arb",
  basis: "Basis",
  volatility: "Volatility",
  macro: "Macro",
  "ml-signal": "ML signal",
};

/** Cryptographic Strykr-verified check. The badge predicate is also
 *  the filter predicate for the "Verified by Strykr only" toggle. */
function isStrykrVerified(s: Strategy): boolean {
  return s.kind === "strykr" && s.signerVerified === true;
}

export function MarketplaceGrid({ catalog, onOpen }: MarketplaceGridProps) {
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState<"All" | StrategyCategory>("All");
  const [sort, setSort] = useState<SortKey>("popular");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  // Multi-select venue filter. Empty Set = all venues pass.
  // OR semantics: a strategy matches if it declares ANY of the
  // selected venues. Same UX model as the dashboard's
  // MarketplacePane.tsx — keep them consistent so users have one
  // mental model across surfaces.
  const [venueFilter, setVenueFilter] = useState<Set<VenueId>>(() => new Set());

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = catalog.filter((s) => {
      if (cat !== "All" && s.category !== cat) return false;
      if (verifiedOnly && !isStrykrVerified(s)) return false;
      if (venueFilter.size > 0) {
        const sv = s.venues ?? [];
        let match = false;
        for (const v of sv) {
          if (venueFilter.has(v as VenueId)) {
            match = true;
            break;
          }
        }
        if (!match) return false;
      }
      if (!q) return true;
      const hay = [
        s.name,
        s.author,
        s.tagline,
        s.description,
        s.tags.join(" "),
        s.assets.join(" "),
        s.category,
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
    list.sort((a, b) => {
      switch (sort) {
        case "popular":
          return b.installs - a.installs;
        case "rating":
          return b.ratings.stars - a.ratings.stars;
        case "newest":
          return Date.parse(b.updatedAt) - Date.parse(a.updatedAt);
        case "price-low":
          return a.priceUsdc - b.priceUsdc;
        case "price-high":
          return b.priceUsdc - a.priceUsdc;
      }
    });
    return list;
  }, [query, cat, sort, verifiedOnly, venueFilter, catalog]);

  const featured = catalog.filter((s) => s.kind === "strykr").slice(0, 2);

  return (
    <section className="mp-page">
      {/* Featured strip — only renders if there are Strykr-kind strategies */}
      {featured.length > 0 && (
        <div className="mp-featured">
          <div className="mp-section-lab">
            <span className="dot" /> Featured · powered by Prism
          </div>
          <div className="mp-feat-grid">
            {featured.map((s) => (
              <FeaturedCard key={s.id} strategy={s} onOpen={() => onOpen(s)} />
            ))}
          </div>
        </div>
      )}

      {/* Filter row */}
      <div className="mp-filters">
        <div className="mp-search">
          <Icon name="search" size={13} />
          <input
            placeholder="Search strategies, authors, tags…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button onClick={() => setQuery("")} aria-label="Clear search" className="mp-clear">
              <Icon name="x" size={11} />
            </button>
          )}
        </div>
        <div className="mp-cat-row" role="tablist" aria-label="Filter by category">
          {ALL_CATEGORIES.map((c) => (
            <button
              key={c}
              role="tab"
              aria-selected={cat === c}
              onClick={() => setCat(c)}
              className={"mp-cat" + (cat === c ? " on" : "")}
            >
              {c === "All" ? "All" : CATEGORY_LABELS[c]}
            </button>
          ))}
        </div>
        <div className="mp-venue-row" role="group" aria-label="Filter by venue">
          <span className="mp-venue-lab">Venues</span>
          {VENUES.map((v) => {
            const on = venueFilter.has(v);
            return (
              <button
                key={v}
                type="button"
                aria-pressed={on}
                onClick={() => {
                  setVenueFilter((prev) => {
                    const next = new Set(prev);
                    if (next.has(v)) next.delete(v);
                    else next.add(v);
                    return next;
                  });
                }}
                className={"mp-venue" + (on ? " on" : "")}
              >
                {VENUE_LABELS[v]}
              </button>
            );
          })}
          {venueFilter.size > 0 && (
            <button
              type="button"
              className="mp-venue-clear"
              onClick={() => setVenueFilter(new Set())}
              aria-label="Clear venue filters"
            >
              clear
            </button>
          )}
        </div>
        <div className="mp-meta-row">
          <label className="mp-toggle">
            <input
              type="checkbox"
              checked={verifiedOnly}
              onChange={(e) => setVerifiedOnly(e.target.checked)}
            />
            <span>Verified by Strykr only</span>
          </label>
          <div className="mp-sort">
            <span className="lbl">Sort</span>
            <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
              {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => (
                <option key={k} value={k}>
                  {SORT_LABELS[k]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Result grid */}
      <div className="mp-grid">
        {filtered.map((s) => (
          <StrategyCard key={s.id} strategy={s} onOpen={() => onOpen(s)} />
        ))}
        {filtered.length === 0 && (
          <div className="mp-empty">
            <Icon name="search" size={20} />
            <div>{catalog.length === 0 ? "No strategies in the catalog yet." : `No strategies match "${query}".`}</div>
            {catalog.length > 0 && (
              <button
                className="mp-btn ghost"
                onClick={() => {
                  setQuery("");
                  setCat("All");
                  setVerifiedOnly(false);
                  setVenueFilter(new Set());
                }}
              >
                Reset filters
              </button>
            )}
          </div>
        )}
      </div>

      <Styles />
    </section>
  );
}

/* ------------------ subcomponents ------------------ */

function FeaturedCard({ strategy, onOpen }: { strategy: Strategy; onOpen: () => void }) {
  return (
    <button className="mp-feat" onClick={onOpen}>
      <div
        className="mp-feat-mono"
        style={{ background: strategy.accentHex + "22", color: strategy.accentHex }}
      >
        {strategy.monogram}
      </div>
      <div className="mp-feat-body">
        <div className="mp-feat-head">
          <span className="badge strykr">
            <Icon name="check" size={10} /> Strykr · Prism
          </span>
        </div>
        <div className="mp-feat-name">{strategy.name}</div>
        <div className="mp-feat-tag">{strategy.tagline}</div>
        <div className="mp-feat-stats">
          <StarRating value={strategy.ratings.stars} count={strategy.ratings.count} size={11} />
          <span className="dot-sep">·</span>
          <span className="mp-feat-installs">{strategy.installs.toLocaleString()} installs</span>
          <span className="dot-sep">·</span>
          <span className="mp-feat-price">
            ${strategy.priceUsdc}
            {strategy.oneTimePayment ? "" : "/mo"}
          </span>
        </div>
      </div>
    </button>
  );
}

function StrategyCard({ strategy, onOpen }: { strategy: Strategy; onOpen: () => void }) {
  const r = strategy.perf.return30dPct;
  const retClass = r >= 0 ? "pos" : "neg";
  const retStr = (r >= 0 ? "+" : "") + r.toFixed(1) + "%";
  const cryptoVerified = isStrykrVerified(strategy);
  return (
    <button className="mp-card" onClick={onOpen}>
      <div className="mp-card-top">
        <div
          className="mp-mono"
          style={{ background: strategy.accentHex + "22", color: strategy.accentHex }}
        >
          {strategy.monogram}
        </div>
        <div className="mp-card-head">
          <div className="mp-card-name-row">
            <span className="mp-card-name">{strategy.name}</span>
            {cryptoVerified && (
              <span
                className="mp-verified"
                title="Verified by Strykr · cryptographic signature pinned at build time"
              >
                <Icon name="check" size={9} />
              </span>
            )}
            {strategy.verified && !cryptoVerified && (
              <span
                className="mp-self-claimed"
                title="Self-claimed by publisher · not cryptographically verified"
              >
                self-claimed
              </span>
            )}
          </div>
          <div className="mp-card-author">by {strategy.author}</div>
        </div>
        <span className={"mp-kind " + strategy.kind}>
          {strategy.kind === "strykr" ? "Strykr" : "Community"}
        </span>
      </div>

      <div className="mp-card-tag">{strategy.tagline}</div>

      <div className="mp-card-tags">
        {strategy.tags.slice(0, 3).map((t) => (
          <span className="mp-tag" key={t}>
            {t}
          </span>
        ))}
      </div>

      <div className="mp-card-perf">
        <div className="cell">
          <div className="k">30d return</div>
          <div className={"v " + retClass}>{retStr}</div>
        </div>
        <div className="cell">
          <div className="k">Sharpe 90d</div>
          <div className="v">{strategy.perf.sharpe90d.toFixed(2)}</div>
        </div>
        <div className="cell">
          <div className="k">Win rate</div>
          <div className="v">{strategy.perf.winRatePct}%</div>
        </div>
      </div>

      <div className="mp-card-foot">
        <VoteWidget strategyId={strategy.id} variant="compact" />
        <span className="mp-installs">
          <Icon name="download" size={10} />
          {strategy.installs.toLocaleString()}
        </span>
        <span className="spacer" />
        {strategy.priceUsdc === 0 ? (
          <span className="mp-price free">Free</span>
        ) : (
          <span className="mp-price">
            ${strategy.priceUsdc}
            {strategy.oneTimePayment ? "" : "/mo"}
          </span>
        )}
      </div>
    </button>
  );
}

function Styles() {
  return (
    <style>{`
      .mp-page { padding: 20px; max-width: 1280px; margin: 0 auto;
        display: flex; flex-direction: column; gap: 24px; }
      .mp-btn { display: inline-flex; align-items: center; gap: 6px;
        background: transparent; border: 1px solid var(--at-hairline); color: var(--at-fg);
        border-radius: var(--at-radius-md); padding: 8px 14px; font-size: 12px;
        font-family: inherit; cursor: pointer;
        transition: all var(--at-dur-fast) var(--at-ease); }
      .mp-btn.ghost:hover { border-color: var(--at-hairline-strong); background: var(--at-bg-hover); }

      /* featured */
      .mp-section-lab { display: flex; align-items: center; gap: 8px;
        font-family: var(--at-font-mono); font-size: 10px; letter-spacing: 0.25em;
        text-transform: uppercase; color: var(--at-muted); margin-bottom: 12px; }
      .mp-section-lab .dot { width: 6px; height: 6px; border-radius: 50%; background: var(--at-accent); box-shadow: 0 0 0 3px rgba(74,222,128,0.15); }
      .mp-feat-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
      @media (max-width: 820px) { .mp-feat-grid { grid-template-columns: 1fr; } }

      .mp-feat { display: grid; grid-template-columns: 56px 1fr;
        gap: 16px; padding: 18px;
        background: linear-gradient(180deg, rgba(74,222,128,0.04), var(--at-surface) 70%);
        border: 1px solid rgba(74,222,128,0.18); border-radius: 14px;
        text-align: left; cursor: pointer; font: inherit; color: inherit;
        transition: border-color var(--at-dur-fast) var(--at-ease), transform var(--at-dur-fast) var(--at-ease); }
      .mp-feat:hover { border-color: rgba(74,222,128,0.35); transform: translateY(-1px); }
      .mp-feat-mono { width: 56px; height: 56px; border-radius: 10px;
        display: flex; align-items: center; justify-content: center;
        font-family: var(--at-font-mono); font-weight: 700; font-size: 18px; letter-spacing: -0.5px; }
      .mp-feat-body { min-width: 0; display: flex; flex-direction: column; gap: 4px; }
      .mp-feat-head { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
      .badge.strykr { display: inline-flex; align-items: center; gap: 4px;
        font-family: var(--at-font-mono); font-size: 9px; letter-spacing: 0.16em;
        text-transform: uppercase; color: var(--at-accent);
        padding: 3px 7px; border-radius: var(--at-radius-full);
        background: rgba(74,222,128,0.1); border: 1px solid rgba(74,222,128,0.22); }
      .mp-feat-name { font-size: 16px; font-weight: 600; color: var(--at-fg); margin-top: 2px; }
      .mp-feat-tag { font-size: 12.5px; color: var(--at-fg-dim); line-height: 1.45; }
      .mp-feat-stats { display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
        font-size: 11px; color: var(--at-muted); margin-top: 6px;
        font-family: var(--at-font-mono); }
      .mp-feat-installs, .mp-feat-price { font-family: var(--at-font-mono); font-variant-numeric: tabular-nums; }
      .mp-feat-price { color: var(--at-fg); font-weight: 600; }
      .dot-sep { color: var(--at-hairline-strong); }

      /* filter */
      .mp-filters { display: flex; flex-direction: column; gap: 10px; }
      .mp-search { display: flex; align-items: center; gap: 10px;
        background: var(--at-surface); border: 1px solid var(--at-hairline);
        border-radius: var(--at-radius-md); padding: 8px 12px;
        transition: border-color var(--at-dur-fast) var(--at-ease); }
      .mp-search:focus-within { border-color: rgba(74,222,128,0.4); }
      .mp-search input { flex: 1; background: transparent; border: 0; outline: none;
        color: var(--at-fg); font-size: 13px; font-family: inherit; }
      .mp-search input::placeholder { color: var(--at-muted); }
      .mp-clear { background: transparent; border: 0; color: var(--at-muted); cursor: pointer; padding: 2px; }
      .mp-clear:hover { color: var(--at-fg); }

      .mp-cat-row { display: flex; gap: 6px; flex-wrap: wrap; }
      .mp-cat { font-family: var(--at-font-mono); font-size: 11px; letter-spacing: 0.04em;
        text-transform: capitalize; padding: 5px 11px;
        background: transparent; border: 1px solid var(--at-hairline);
        border-radius: var(--at-radius-full); color: var(--at-fg-dim);
        cursor: pointer; font-weight: 500;
        transition: all var(--at-dur-fast) var(--at-ease); }
      .mp-cat:hover { border-color: var(--at-hairline-strong); color: var(--at-fg); }
      .mp-cat.on { background: rgba(74,222,128,0.08); border-color: rgba(74,222,128,0.4); color: var(--at-accent); }

      .mp-venue-row { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
      .mp-venue-lab { font-family: var(--at-font-mono); font-size: 9px;
        letter-spacing: 0.2em; text-transform: uppercase; color: var(--at-muted);
        margin-right: 4px; }
      .mp-venue { font-family: var(--at-font-mono); font-size: 11px; letter-spacing: 0.04em;
        padding: 4px 10px;
        background: transparent; border: 1px solid var(--at-hairline);
        border-radius: var(--at-radius-full); color: var(--at-fg-dim);
        cursor: pointer; font-weight: 500;
        transition: all var(--at-dur-fast) var(--at-ease); }
      .mp-venue:hover { border-color: var(--at-hairline-strong); color: var(--at-fg); }
      .mp-venue.on { background: rgba(96,165,250,0.08); border-color: rgba(96,165,250,0.4); color: #93c5fd; }
      .mp-venue-clear { font-family: var(--at-font-mono); font-size: 9.5px;
        letter-spacing: 0.16em; text-transform: uppercase;
        background: transparent; border: 0; color: var(--at-muted);
        cursor: pointer; padding: 4px 6px; }
      .mp-venue-clear:hover { color: var(--at-fg); }

      .mp-meta-row { display: flex; justify-content: space-between; align-items: center; gap: 16px; flex-wrap: wrap; }
      .mp-toggle { display: inline-flex; align-items: center; gap: 6px;
        font-size: 12px; color: var(--at-fg-dim); cursor: pointer; user-select: none; }
      .mp-toggle input { accent-color: var(--at-accent); }
      .mp-sort { display: inline-flex; align-items: center; gap: 8px;
        font-size: 12px; color: var(--at-muted); }
      .mp-sort .lbl { font-family: var(--at-font-mono); font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase; }
      .mp-sort select { background: var(--at-surface); border: 1px solid var(--at-hairline);
        color: var(--at-fg); border-radius: var(--at-radius-sm); padding: 5px 8px;
        font-family: inherit; font-size: 12px; outline: none; cursor: pointer; }
      .mp-sort select:hover { border-color: var(--at-hairline-strong); }

      /* grid */
      .mp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 14px; }
      .mp-card { display: flex; flex-direction: column; gap: 12px;
        padding: 16px; background: var(--at-surface);
        border: 1px solid var(--at-hairline); border-radius: 12px;
        text-align: left; cursor: pointer; font: inherit; color: inherit;
        transition: border-color var(--at-dur-fast) var(--at-ease),
          transform var(--at-dur-fast) var(--at-ease); }
      .mp-card:hover { border-color: var(--at-hairline-strong); transform: translateY(-1px); }

      .mp-card-top { display: grid; grid-template-columns: 36px 1fr auto; gap: 10px; align-items: center; }
      .mp-mono { width: 36px; height: 36px; border-radius: 8px;
        display: flex; align-items: center; justify-content: center;
        font-family: var(--at-font-mono); font-weight: 700; font-size: 13px; letter-spacing: -0.3px; }
      .mp-card-head { min-width: 0; }
      .mp-card-name-row { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
      .mp-card-name { font-size: 14px; font-weight: 600; color: var(--at-fg); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .mp-verified { display: inline-flex; align-items: center; justify-content: center;
        width: 14px; height: 14px; border-radius: 50%;
        background: var(--at-accent); color: var(--at-bg); flex-shrink: 0; cursor: help; }
      .mp-self-claimed { font-family: var(--at-font-mono); font-size: 8.5px;
        letter-spacing: 0.16em; text-transform: uppercase;
        color: var(--at-muted); padding: 2px 6px; border-radius: var(--at-radius-sm);
        background: var(--at-bg); border: 1px dashed var(--at-hairline);
        flex-shrink: 0; cursor: help; }
      .mp-card-author { font-family: var(--at-font-mono); font-size: 10.5px; color: var(--at-muted);
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .mp-kind { font-family: var(--at-font-mono); font-size: 9px; letter-spacing: 0.18em;
        text-transform: uppercase; padding: 2px 7px; border-radius: var(--at-radius-full);
        background: var(--at-bg); border: 1px solid var(--at-hairline); color: var(--at-muted); }
      .mp-kind.strykr { color: var(--at-accent); border-color: rgba(74,222,128,0.2); background: rgba(74,222,128,0.06); }

      .mp-card-tag { font-size: 12.5px; color: var(--at-fg-dim); line-height: 1.45;
        display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }

      .mp-card-tags { display: flex; gap: 4px; flex-wrap: wrap; }
      .mp-tag { font-family: var(--at-font-mono); font-size: 10px; padding: 2px 7px;
        background: var(--at-bg); border: 1px solid var(--at-hairline);
        border-radius: var(--at-radius-sm); color: var(--at-muted); }

      .mp-card-perf { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px;
        background: var(--at-hairline); border: 1px solid var(--at-hairline);
        border-radius: var(--at-radius-md); overflow: hidden; }
      .mp-card-perf .cell { background: var(--at-surface); padding: 8px 10px; }
      .mp-card-perf .k { font-family: var(--at-font-mono); font-size: 9px;
        letter-spacing: 0.18em; text-transform: uppercase; color: var(--at-muted); margin-bottom: 2px; }
      .mp-card-perf .v { font-family: var(--at-font-mono); font-size: 13px;
        font-variant-numeric: tabular-nums; color: var(--at-fg); font-weight: 500; }
      .mp-card-perf .v.pos { color: var(--at-accent); }
      .mp-card-perf .v.neg { color: var(--at-danger); }

      .mp-card-foot { display: flex; align-items: center; gap: 12px;
        padding-top: 8px; border-top: 1px solid var(--at-hairline);
        font-size: 11px; color: var(--at-muted); }
      .mp-card-foot .spacer { flex: 1; }
      .mp-installs { display: inline-flex; align-items: center; gap: 4px;
        font-family: var(--at-font-mono); font-variant-numeric: tabular-nums; }
      .mp-price { font-family: var(--at-font-mono); font-size: 12px; font-weight: 600; color: var(--at-fg); }
      .mp-price.free { color: var(--at-accent); }

      .mp-empty { grid-column: 1 / -1; display: flex; flex-direction: column;
        align-items: center; gap: 8px; padding: 48px 20px;
        color: var(--at-muted); text-align: center; }
      .mp-empty .mp-btn { margin-top: 8px; }
    `}</style>
  );
}
